import { useMemo } from 'react';
import { Container, Icon, Popover } from '@amzn/awsui-components-react';
import { normalizeSfdcStage } from '../../../utils/sfdcStage';
import StagePieChart from './StagePieChart';
import StageLegend from './StageLegend';

interface KpiBucket {
  oppCount?: number;
  arr?: number;
}

interface AssetRow {
  assetId: string;
  assetName: string;
  useCase?: string;
  subTopics?: string[];
  sfdcTag?: string;
}

interface Props {
  pipelineTotal: number;
  totalArr: number;
  taggedPipeline?: KpiBucket;
  pendingTagPipeline?: KpiBucket;
  opps: Array<{ oppStage?: string }>;
  assets?: AssetRow[];
}

function formatCurrency(val: number | undefined | null): string {
  if (val == null || val === 0) return '$ 0';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1000) return `$${Math.round(val / 1000).toLocaleString('en-US')}K`;
  return `$${Math.round(val).toLocaleString('en-US')}`;
}

function formatKpiBucket(bucket?: KpiBucket): string {
  if (!bucket) return '0 opps, ARR $ 0';
  return `${bucket.oppCount ?? 0} opps, ARR ${formatCurrency(bucket.arr)}`;
}

function AssetTagsPopoverContent({ asset }: { asset: AssetRow }) {
  const hasUseCase = !!asset.useCase;
  const hasSubTopics = (asset.subTopics?.length ?? 0) > 0;
  const hasSfdcTag = !!asset.sfdcTag;

  if (!hasUseCase && !hasSubTopics && !hasSfdcTag) {
    return <div className="ivt-asset-tags__empty">No classification tags</div>;
  }

  return (
    <div className="ivt-asset-tags">
      {hasUseCase && (
        <div className="ivt-asset-tags__group">
          <span className="ivt-asset-tags__group-label">Use Case</span>
          <span className="ivt-asset-tags__chip ivt-asset-tags__chip--usecase">
            {asset.useCase}
          </span>
        </div>
      )}
      {hasSubTopics && (
        <div className="ivt-asset-tags__group">
          <span className="ivt-asset-tags__group-label">Sub-topic</span>
          <div className="ivt-asset-tags__chip-list">
            {asset.subTopics!.map((t) => (
              <span
                key={t}
                className="ivt-asset-tags__chip ivt-asset-tags__chip--topic"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
      {hasSfdcTag && (
        <div className="ivt-asset-tags__group">
          <span className="ivt-asset-tags__group-label">SFDC Tag</span>
          <span className="ivt-asset-tags__chip ivt-asset-tags__chip--sfdc">
            {asset.sfdcTag}
          </span>
        </div>
      )}
    </div>
  );
}

export default function InformationCard({
  pipelineTotal, taggedPipeline, pendingTagPipeline, opps, assets,
}: Props) {
  const stageCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of opps) {
      const s = normalizeSfdcStage(o.oppStage ?? 'Others');
      m.set(s, (m.get(s) ?? 0) + 1);
    }
    return m;
  }, [opps]);

  return (
    <Container>
      <div className="ivt-info-card">
        {/* Left section */}
        <div className="ivt-info-card__left">
          {/* Pipeline Information */}
          <div className="ivt-info-card__section">
            <div className="ivt-info-card__section-title">Pipeline Information</div>
            <div className="ivt-info-card__kpi-row">
              <div className="ivt-info-card__kv">
                <span className="ivt-info-card__kv-key">Tagged Pipeline</span>
                <span className="ivt-info-card__kv-value">{formatKpiBucket(taggedPipeline)}</span>
              </div>
              <div className="ivt-info-card__kv">
                <span className="ivt-info-card__kv-key">Pending Tag Pipeline</span>
                <span className="ivt-info-card__kv-value">{formatKpiBucket(pendingTagPipeline)}</span>
              </div>
            </div>
          </div>

          {/* Asset Information */}
          {assets && assets.length > 0 && (
            <div className="ivt-info-card__section">
              <div className="ivt-info-card__section-title">Asset Information</div>
              <table className="ivt-info-card__asset-table">
                <tbody>
                  {assets.map((a) => (
                    <tr key={a.assetId}>
                      <td>
                        <span className="ivt-info-card__asset-cell">
                          <span>{a.assetName}</span>
                          <Popover
                            position="right"
                            size="large"
                            triggerType="custom"
                            dismissButton={false}
                            renderWithPortal
                            content={<AssetTagsPopoverContent asset={a} />}
                          >
                            <button
                              type="button"
                              className="ivt-info-card__asset-info-btn"
                              aria-label={`View classification tags for ${a.assetName}`}
                            >
                              <Icon name="status-info" variant="link" />
                            </button>
                          </Popover>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right section: Donut + Legend */}
        <div className="ivt-info-card__right">
          <StagePieChart stageCounts={stageCounts} pipelineTotal={pipelineTotal} />
          <StageLegend />
        </div>
      </div>
    </Container>
  );
}
